import React, { Fragment, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import Header from '../../../components/Header';
import Appfooter from '../../../components/Appfooter';
import Load from '../../../components/Load';
import {
  DISPONIBILIDADE_QUADRAS_BATCH,
  todayISO,
  formatCurrency,
} from '../../../services/quadraService';
import { QUADRAS_POR_LOCAL, AGENDAMENTOS_POR_QUADRA_E_DATA, CRIAR_AGENDAMENTO_ADMIN, BUSCAR_USUARIOS, CONFIRMAR_PAGAMENTO_ADMIN, CANCELAR_AGENDAMENTO_ADMIN } from '../../../services/quadraAdminService';
import { LOCAL_POR_ID } from '../../../services/localService';

const SALDO_CREDITO_USUARIO = gql`
  query SaldoCreditoUsuarioNoLocal($usuarioId: ID!, $localId: ID!) {
    saldoCreditoUsuarioNoLocal(usuarioId: $usuarioId, localId: $localId)
  }
`;

const DIAS_PT = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const formatTelefone = (tel?: string | null) => {
  if (!tel) return '';
  const digits = tel.replace(/\D/g, '');
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return tel;
};

const addDays = (iso: string, n: number) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

const AdminAgendaLocal: React.FC = () => {
  const { localId } = useParams<{ localId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState(todayISO());
  const [agSelecionado, setAgSelecionado] = useState<any | null>(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [valorPagoInput, setValorPagoInput] = useState('');
  const [agendamentosMap, setAgendamentosMap] = useState<Record<string, any[]>>({});
  const [modalAgendar, setModalAgendar] = useState<{ quadraId: string; quadraNome: string; horaInicio: string } | null>(null);
  const [formHoraFim, setFormHoraFim] = useState('');
  const [formObservacao, setFormObservacao] = useState('');
  const [formUsuarioId, setFormUsuarioId] = useState('');
  const [formUsarCredito, setFormUsarCredito] = useState(false);
  const [saldoUsuario, setSaldoUsuario] = useState<number>(0);
  const [buscaUsuario, setBuscaUsuario] = useState('');
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<{ id: string; nome: string; email: string } | null>(null);
  const [erroAgendar, setErroAgendar] = useState('');

  const [criarAgendamento, { loading: criandoAg }] = useMutation(CRIAR_AGENDAMENTO_ADMIN);
  const [confirmarPagamento, { loading: confirmandoPgto }] = useMutation(CONFIRMAR_PAGAMENTO_ADMIN);
  const [cancelarAgendamento, { loading: cancelando }] = useMutation(CANCELAR_AGENDAMENTO_ADMIN);
  const [buscarUsuariosQuery, { data: usuariosData, loading: buscandoUsuarios }] = useLazyQuery(BUSCAR_USUARIOS, { fetchPolicy: 'network-only' });
  const [buscarSaldoUsuario] = useLazyQuery(SALDO_CREDITO_USUARIO, { fetchPolicy: 'network-only' });

  React.useEffect(() => {
    if (buscaUsuario.length < 2) return;
    const t = setTimeout(() => buscarUsuariosQuery({ variables: { nome: buscaUsuario } }), 350);
    return () => clearTimeout(t);
  }, [buscaUsuario, buscarUsuariosQuery]);

  const { data: localData } = useQuery(LOCAL_POR_ID, {
    variables: { id: localId },
    fetchPolicy: 'cache-first',
  });
  const local = localData?.localPorId;

  const { data: quadrasData, loading: loadingQuadras } = useQuery(QUADRAS_POR_LOCAL, {
    variables: { localId },
    fetchPolicy: 'cache-and-network',
  });
  const quadras: any[] = (quadrasData?.quadrasPorLocal ?? []).filter((q: any) => q.ativa);

  const quadraIds = quadras.map((q: any) => q.id);
  const { data: batchData, loading: loadingDisp, refetch: refetchDisp } = useQuery(DISPONIBILIDADE_QUADRAS_BATCH, {
    variables: { quadraIds, data },
    skip: quadraIds.length === 0,
    fetchPolicy: 'network-only',
  });
  const batchDisp: any[] = batchData?.disponibilidadeQuadrasBatch ?? [];

  // Lazy query para buscar agendamentos
  const [buscarAgendamentos] = useLazyQuery(AGENDAMENTOS_POR_QUADRA_E_DATA, { fetchPolicy: 'network-only' });

  const [refreshKey, setRefreshKey] = useState(0);

  // Pre-fetch agendamentos de todas as quadras quando disponibilidade muda
  React.useEffect(() => {
    if (quadraIds.length === 0 || loadingDisp) return;
    const fetchAll = async () => {
      const map: Record<string, any[]> = {};
      for (const qId of quadraIds) {
        try {
          const { data: agData } = await buscarAgendamentos({ variables: { quadraId: qId, data } });
          map[qId] = agData?.agendamentosPorQuadraEData ?? [];
        } catch { map[qId] = []; }
      }
      setAgendamentosMap(map);
    };
    fetchAll();
  }, [quadraIds.join(','), data, loadingDisp, refreshKey]); // eslint-disable-line

  // Gera faixas de horário (unifica todos os slots de todas as quadras).
  // Além dos slots de disponibilidade, inclui os horários dos agendamentos já
  // existentes — assim datas passadas (sem slots disponíveis) e agendamentos
  // fora do horário permitido continuam aparecendo na grade.
  const todosHorarios = new Set<string>();
  batchDisp.forEach((d: any) => {
    d.slots?.forEach((s: any) => {
      todosHorarios.add(s.horaInicio);
    });
  });
  // Expande cada agendamento em incrementos de 30 min para cobrir toda a faixa.
  const addMinutos = (hhmm: string, min: number) => {
    const [h, m] = hhmm.split(':').map(Number);
    const total = h * 60 + m + min;
    const hh = Math.floor(total / 60) % 24;
    const mm = total % 60;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };
  Object.values(agendamentosMap).forEach((ags: any[]) => {
    (ags ?? []).forEach((a: any) => {
      if (!a?.horaInicio || !a?.horaFim) return;
      if (!['RESERVADO', 'CONFIRMADO', 'A_PAGAR', 'CONCLUIDO'].includes(a.status)) return;
      let cursor = a.horaInicio.slice(0, 5);
      const fim = a.horaFim.slice(0, 5);
      let guard = 0;
      while (cursor < fim && guard < 96) {
        todosHorarios.add(cursor);
        cursor = addMinutos(cursor, 30);
        guard++;
      }
    });
  });
  const horarios = Array.from(todosHorarios).sort();

  const diaSemana = DIAS_PT[new Date(data + 'T12:00:00').getDay()];

  // Busca slot da disponibilidade
  const getSlot = (quadraId: string, horaInicio: string) => {
    const entry = batchDisp.find((d: any) => d.quadraId === quadraId);
    if (!entry) return null;
    return entry.slots?.find((s: any) => s.horaInicio === horaInicio) ?? null;
  };

  // Busca agendamento que ocupa um slot
  const getAgendamento = (quadraId: string, horaInicio: string) => {
    const ags = agendamentosMap[quadraId] ?? [];
    return ags.find((a: any) =>
      a.horaInicio <= horaInicio && a.horaFim > horaInicio &&
      ['RESERVADO', 'CONFIRMADO', 'A_PAGAR', 'CONCLUIDO'].includes(a.status)
    );
  };

  // Formata nome: "João S."
  const formatNome = (nome?: string) => {
    if (!nome) return '';
    const partes = nome.trim().split(/\s+/);
    if (partes.length === 1) return partes[0];
    return `${partes[0]} ${partes[1][0]}.`;
  };

  // Ao clicar num slot reservado
  const handleSlotReservado = (quadraId: string, horaInicio: string) => {
    const ag = getAgendamento(quadraId, horaInicio);
    if (ag) setAgSelecionado(ag);
  };

  // Ao clicar num slot disponível
  const handleSlotDisponivel = (quadraId: string, quadraNome: string, horaInicio: string) => {
    // Encontra horários disponíveis consecutivos a partir deste
    setModalAgendar({ quadraId, quadraNome, horaInicio });
    // Sugere horaFim = próximo slot após horaInicio (1 slot à frente)
    const idx = horarios.indexOf(horaInicio);
    const sugestao = idx + 2 < horarios.length ? horarios[idx + 2] : (idx + 1 < horarios.length ? horarios[idx + 1] : horaInicio);
    setFormHoraFim(sugestao);
  };

  // Horários disponíveis para fim (todos os que são >= horaInicio do modal)
  const horariosFinDisponiveis = () => {
    if (!modalAgendar) return [];
    const entry = batchDisp.find((d: any) => d.quadraId === modalAgendar.quadraId);
    if (!entry) return [];
    const slots = entry.slots ?? [];
    const idx = slots.findIndex((s: any) => s.horaInicio === modalAgendar.horaInicio);
    if (idx < 0) return [];
    // Pega slots consecutivos disponíveis a partir do selecionado
    const resultado: string[] = [];
    for (let i = idx; i < slots.length; i++) {
      if (!slots[i].disponivel) break;
      resultado.push(slots[i].horaFim);
    }
    return resultado;
  };

  const loading = loadingQuadras || loadingDisp;

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">

            {/* Header */}
            <div className="card shadow-xss w-100 border-0 p-4 mb-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <button className="btn btn-light btn-sm rounded-xl" onClick={() => navigate('/admin/quadras')}>
                  <i className="feather-arrow-left me-1"></i> Voltar
                </button>
                <div>
                  <h2 className="fw-700 mb-0 font-md text-grey-900">
                    <i className="feather-calendar me-2 text-primary"></i>
                    Agenda Geral
                  </h2>
                  <span className="font-xssss text-grey-500">{local?.nome ?? 'Local'}</span>
                </div>
              </div>

              {/* Seletor de data */}
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-light rounded-xl px-2"
                  style={{ height: 38 }}
                  onClick={() => setData(addDays(data, -1))}
                ><i className="feather-chevron-left"></i></button>
                <input
                  type="date"
                  className="form-control rounded-xl font-xsss"
                  value={data}
                  onChange={e => setData(e.target.value || todayISO())}
                  style={{ width: 155, height: 38 }}
                />
                <button
                  className="btn btn-light rounded-xl px-2"
                  style={{ height: 38 }}
                  onClick={() => setData(addDays(data, 1))}
                ><i className="feather-chevron-right"></i></button>
                <span className="font-xssss text-grey-500 ms-2">{diaSemana}</span>
              </div>
            </div>

            {loading && <Load />}

            {!loading && quadras.length === 0 && (
              <div className="card shadow-xss p-4 text-center border-0">
                <p className="text-grey-500 font-xss">Nenhuma quadra ativa neste local.</p>
              </div>
            )}

            {!loading && quadras.length > 0 && horarios.length > 0 && (
              <div className="card shadow-xss rounded-xxl border-0 p-3 mb-3">
                {/* Legenda */}
                <div className="d-flex gap-3 mb-3">
                  <span className="d-flex align-items-center gap-1 font-xssss text-grey-500">
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: '#dcfce7', border: '1px solid #22c55e', display: 'inline-block' }}></span> Disponível
                  </span>
                  <span className="d-flex align-items-center gap-1 font-xssss text-grey-500">
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: '#fef3c7', border: '1px solid #f59e0b', display: 'inline-block' }}></span> Reservado
                  </span>
                  <span className="d-flex align-items-center gap-1 font-xssss text-grey-500">
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: '#e5e7eb', border: '1px solid #9ca3af', display: 'inline-block' }}></span> Bloqueado
                  </span>
                </div>

                {/* Grade */}
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th className="text-grey-500 font-xssss fw-600" style={{ width: 65, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>Horário</th>
                        {quadras.map((q: any) => (
                          <th key={q.id} className="text-center font-xssss fw-700 text-grey-900" style={{ minWidth: 100 }}>
                            <a
                              href="#"
                              className="text-grey-900 text-decoration-none"
                              style={{ cursor: 'pointer', borderBottom: '1px dashed #aaa' }}
                              onClick={(e) => { e.preventDefault(); navigate(`/admin/quadras/${q.id}/agenda`); }}
                            >
                              {q.nome}
                            </a>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {horarios.map((hora) => (
                        <tr key={hora}>
                          <td className="font-xssss fw-600 text-grey-600 align-middle" style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>{hora}</td>
                          {quadras.map((q: any) => {
                            const slot = getSlot(q.id, hora);
                            const bloqueado = !!slot && !slot.disponivel && slot.motivo === 'BLOQUEADO';
                            // Busca o agendamento que ocupa este horário, independentemente
                            // de haver slot (datas passadas não retornam slots).
                            const ag = !bloqueado ? getAgendamento(q.id, hora) : null;

                            let bg = '#f9fafb';
                            let cursor = 'default';
                            let title = '';

                            if (slot?.disponivel) {
                              bg = '#dcfce7';
                              cursor = 'pointer';
                              title = 'Disponível — clique para agendar';
                            } else if (bloqueado) {
                              bg = '#e5e7eb';
                              title = 'Bloqueado';
                            } else if (ag || (slot && !slot.disponivel)) {
                              bg = '#fef3c7';
                              cursor = ag ? 'pointer' : 'default';
                              title = ag ? 'Reservado — clique para ver detalhes' : 'Reservado';
                            }

                            return (
                              <td
                                key={q.id}
                                className="text-center align-middle"
                                style={{
                                  background: bg,
                                  cursor,
                                  padding: '4px 2px',
                                  borderColor: '#e5e7eb',
                                }}
                                title={title}
                                onClick={() => {
                                  if (slot && slot.disponivel) {
                                    handleSlotDisponivel(q.id, q.nome, hora);
                                  } else if (ag) {
                                    setAgSelecionado(ag);
                                  }
                                }}
                              >
                                {ag && (
                                  <span className="font-xssss fw-600" style={{ color: '#92400e', fontSize: 10 }}>
                                    {formatNome(ag.usuario?.nome)}
                                  </span>
                                )}
                                {!ag && slot && !slot.disponivel && slot.motivo !== 'BLOQUEADO' && (
                                  <i className="feather-user text-warning" style={{ fontSize: 11 }}></i>
                                )}
                                {bloqueado && (
                                  <i className="feather-lock text-grey-400" style={{ fontSize: 10 }}></i>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!loading && quadras.length > 0 && horarios.length === 0 && (
              <div className="card shadow-xss p-4 text-center border-0">
                <p className="text-grey-500 font-xss">Nenhum horário configurado para esta data.</p>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Modal detalhe do agendamento */}
      {agSelecionado && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setAgSelecionado(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content rounded-xxl border-0 shadow-lg p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="fw-700 font-xss mb-0">Detalhes da Reserva</h5>
                <button className="btn-close" onClick={() => setAgSelecionado(null)} />
              </div>

              <div className="bg-greylight rounded-xl p-3 mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="font-xssss text-grey-500">Usuário</span>
                  <span className="font-xssss fw-600">{agSelecionado.usuario?.nome}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="font-xssss text-grey-500">Email</span>
                  <span className="font-xssss fw-600">{agSelecionado.usuario?.email ?? '—'}</span>
                </div>
                {agSelecionado.usuario?.atleta?.telefone && (
                  <div className="d-flex justify-content-between mb-1">
                    <span className="font-xssss text-grey-500">Telefone</span>
                    <span className="font-xssss fw-600">{formatTelefone(agSelecionado.usuario.atleta.telefone)}</span>
                  </div>
                )}
                <div className="d-flex justify-content-between mb-1">
                  <span className="font-xssss text-grey-500">Horário</span>
                  <span className="font-xssss fw-600">{agSelecionado.horaInicio} – {agSelecionado.horaFim}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="font-xssss text-grey-500">Status</span>
                  <span className={`badge font-xssss rounded-xl ${agSelecionado.status === 'CONFIRMADO' ? 'bg-success' : 'bg-warning'}`}>
                    {agSelecionado.status}
                  </span>
                </div>
                {agSelecionado.valor != null && (
                  <div className="d-flex justify-content-between mb-1">
                    <span className="font-xssss text-grey-500">Valor</span>
                    <span className="font-xssss fw-700 text-success">{formatCurrency(agSelecionado.valor)}</span>
                  </div>
                )}
                {agSelecionado.observacao && (
                  <div className="d-flex justify-content-between">
                    <span className="font-xssss text-grey-500">Observação</span>
                    <span className="font-xssss fw-600">{agSelecionado.observacao}</span>
                  </div>
                )}
              </div>

              {['A_PAGAR', 'RESERVADO'].includes(agSelecionado.status) && (
                <div className="border-top pt-3 mt-2 mb-2">
                  <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Valor pago (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control rounded-xl font-xsss mb-2"
                    placeholder={agSelecionado.valor != null ? String(agSelecionado.valor) : '0.00'}
                    value={valorPagoInput}
                    onChange={e => setValorPagoInput(e.target.value)}
                  />
                  <p className="font-xssss text-grey-500 mb-2">
                    Informe o valor efetivamente recebido. Deixe vazio para usar o valor original ({agSelecionado.valor != null ? formatCurrency(agSelecionado.valor) : '—'}).
                    {valorPagoInput === '0' && ' Se zero, o usuário não receberá crédito em caso de cancelamento.'}
                  </p>
                  <button
                    className="btn bg-success text-white rounded-xl font-xssss fw-600 w-100"
                    onClick={async () => {
                      try {
                        const vp = valorPagoInput ? parseFloat(valorPagoInput) : undefined;
                        await confirmarPagamento({ variables: { id: agSelecionado.id, valorPago: vp } });
                        setAgSelecionado(null);
                        setValorPagoInput('');
                        refetchDisp();
                        setRefreshKey(k => k + 1);
                      } catch (err: any) {
                        alert(err?.graphQLErrors?.[0]?.message ?? err.message ?? 'Erro ao confirmar pagamento.');
                      }
                    }}
                    disabled={confirmandoPgto}
                  >
                    {confirmandoPgto ? 'Confirmando...' : <><i className="feather-check me-1"></i>Confirmar pagamento</>}
                  </button>
                </div>
              )}

              {['RESERVADO', 'A_PAGAR', 'CONFIRMADO'].includes(agSelecionado.status) && (
                <button
                  className="btn btn-outline-danger rounded-xl font-xssss fw-600 w-100 mb-2"
                  onClick={async () => {
                    const pago = agSelecionado.status === 'CONFIRMADO';
                    const msg = pago
                      ? `Cancelar este agendamento? O usuário receberá R$ ${agSelecionado.valor ?? 0} em crédito neste local.`
                      : 'Cancelar este agendamento e liberar o horário?';
                    if (!window.confirm(msg)) return;
                    try {
                      await cancelarAgendamento({ variables: { id: agSelecionado.id, motivo: 'Cancelado pelo administrador' } });
                      setAgSelecionado(null);
                      refetchDisp();
                      setRefreshKey(k => k + 1);
                    } catch (err: any) {
                      alert(err?.graphQLErrors?.[0]?.message ?? err.message ?? 'Erro ao cancelar.');
                    }
                  }}
                  disabled={cancelando}
                >
                  {cancelando ? 'Cancelando...' : <><i className="feather-x me-1"></i>Cancelar agendamento</>}
                </button>
              )}

              <button
                className="btn btn-light rounded-xl font-xssss fw-600 w-100"
                onClick={() => setAgSelecionado(null)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal agendar horário */}
      {modalAgendar && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setModalAgendar(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content rounded-xxl border-0 shadow-lg p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="fw-700 font-xss mb-0">Agendamento Manual</h5>
                <button className="btn-close" onClick={() => { setModalAgendar(null); setErroAgendar(''); }} />
              </div>

              <div className="bg-greylight rounded-xl p-3 mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="font-xssss text-grey-500">Quadra</span>
                  <span className="font-xssss fw-600">{modalAgendar.quadraNome}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="font-xssss text-grey-500">Data</span>
                  <span className="font-xssss fw-600">{new Date(data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })} — {diaSemana}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="font-xssss text-grey-500">Início</span>
                  <span className="font-xssss fw-600">{modalAgendar.horaInicio}</span>
                </div>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                setErroAgendar('');
                if (!formObservacao.trim()) { setErroAgendar('Observação é obrigatória para agendamento manual.'); return; }
                try {
                  await criarAgendamento({
                    variables: {
                      input: {
                        quadraId: modalAgendar.quadraId,
                        dataAgendamento: data,
                        horaInicio: modalAgendar.horaInicio,
                        horaFim: formHoraFim,
                        observacao: formObservacao,
                        usuarioId: formUsuarioId || undefined,
                        usarCredito: formUsarCredito || undefined,
                      },
                    },
                  });
                  setModalAgendar(null);
                  setFormObservacao('');
                  setFormUsuarioId('');
                  setFormUsarCredito(false);
                  setSaldoUsuario(0);
                  setUsuarioSelecionado(null);
                  setBuscaUsuario('');
                  // Refresh da grade sem recarregar a página
                  refetchDisp();
                  setRefreshKey(k => k + 1);
                } catch (err: any) {
                  setErroAgendar(err?.graphQLErrors?.[0]?.message ?? err.message ?? 'Erro ao criar agendamento.');
                }
              }}>
                <div className="mb-2">
                  <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Até que horário? *</label>
                  <select
                    className="form-select rounded-xl font-xsss"
                    value={formHoraFim}
                    onChange={e => setFormHoraFim(e.target.value)}
                    required
                  >
                    {horariosFinDisponiveis().map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-2">
                  <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Usuário (opcional)</label>
                  {usuarioSelecionado ? (
                    <div className="d-flex align-items-center gap-2 bg-greylight rounded-xl px-3 py-2">
                      <span className="font-xssss fw-600">{usuarioSelecionado.nome}</span>
                      <span className="font-xssss text-grey-500">{usuarioSelecionado.email}</span>
                      <button type="button" className="btn btn-sm btn-light ms-auto rounded-xl" onClick={() => { setUsuarioSelecionado(null); setFormUsuarioId(''); setSaldoUsuario(0); setFormUsarCredito(false); }}>
                        <i className="feather-x" style={{ fontSize: 12 }}></i>
                      </button>
                    </div>
                  ) : (
                    <div className="position-relative">
                      <input
                        className="form-control rounded-xl font-xsss"
                        placeholder="Buscar por nome..."
                        value={buscaUsuario}
                        onChange={e => setBuscaUsuario(e.target.value)}
                      />
                      {buscandoUsuarios && <small className="text-grey-400">Buscando...</small>}
                      {usuariosData?.usuariosPorNome?.length > 0 && buscaUsuario.length >= 2 && (
                        <div className="position-absolute bg-white shadow rounded-xl w-100 mt-1" style={{ zIndex: 10, maxHeight: 150, overflowY: 'auto' }}>
                          {usuariosData.usuariosPorNome.map((u: any) => (
                            <div
                              key={u.id}
                              className="px-3 py-2 border-bottom font-xssss"
                              style={{ cursor: 'pointer' }}
                              onClick={async () => {
                                setUsuarioSelecionado(u);
                                setFormUsuarioId(u.id);
                                setBuscaUsuario('');
                                // Buscar saldo do usuário neste local
                                if (localId) {
                                  try {
                                    const { data: sd } = await buscarSaldoUsuario({ variables: { usuarioId: u.id, localId } });
                                    setSaldoUsuario(sd?.saldoCreditoUsuarioNoLocal ?? 0);
                                  } catch { setSaldoUsuario(0); }
                                }
                              }}
                            >
                              <span className="fw-600">{u.nome}</span> <span className="text-grey-500">{u.email}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Observação *</label>
                  <textarea
                    className="form-control rounded-xl font-xsss"
                    rows={2}
                    value={formObservacao}
                    onChange={e => setFormObservacao(e.target.value)}
                    placeholder="Motivo do agendamento manual"
                    required
                  />
                </div>

                {usuarioSelecionado && saldoUsuario > 0 && (
                  <div className="mb-3 bg-greylight rounded-xl p-3">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="font-xssss text-grey-600">
                        <i className="bi bi-wallet2 me-1"></i>Crédito de {usuarioSelecionado.nome.split(' ')[0]}
                      </span>
                      <span className="font-xssss fw-700 text-success">{formatCurrency(saldoUsuario)}</span>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="usarCreditoSwitch"
                        checked={formUsarCredito}
                        onChange={e => setFormUsarCredito(e.target.checked)}
                      />
                      <label className="form-check-label font-xssss fw-600 text-grey-700" htmlFor="usarCreditoSwitch">
                        Abater crédito no pagamento
                      </label>
                    </div>
                  </div>
                )}

                {erroAgendar && <div className="alert alert-danger font-xssss py-2 mb-2">{erroAgendar}</div>}

                <div className="d-flex gap-2">
                  <button type="submit" className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 flex-grow-1" disabled={criandoAg}>
                    {criandoAg ? 'Criando...' : <><i className="feather-plus me-1"></i>Criar agendamento</>}
                  </button>
                  <button type="button" className="btn btn-light rounded-xl font-xssss fw-600" onClick={() => { setModalAgendar(null); setErroAgendar(''); }}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {loadingDetalhe && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2000 }}>
          <Load />
        </div>
      )}

      <Appfooter />
    </Fragment>
  );
};

export default AdminAgendaLocal;
